import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Loader2, Info, CalendarDays, RefreshCw, HandCoins,
    ChevronLeft, Eye, EyeOff, TrendingUp, TrendingDown,
    FileText, User, Download, Filter
} from 'lucide-react';
import { useToast } from '../hooks/useToast';
import { fetchCustomerLedgerReport, currencyCodeToSymbolMap, fetchCustomerLedgerExportCSV, salesSourceDocument } from '../apiHelper';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import TransactionTooltip from '../components/ui/TransactionTooltip';
import Modal from '../components/ui/Modal';
import SalesSourceDocument from './SalesSourceDocument';

// Utility to format date strings
// const formatDate = (date, includeTime = true) => {
//     if (!date) return '';
//     const options = { year: 'numeric', month: 'short', day: 'numeric' };
//     if (includeTime) {
//         options.hour = '2-digit';
//         options.minute = '2-digit';
//         options.hour12 = false;
//     }
//     return new Date(date).toLocaleString('en-US', options).replace(',', '');
// };
const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);

    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // Set to false if you prefer 24h format
    }).format(date);
};

const formatCurrency = (amount, currencyCode) => {
    if (amount === null || amount === undefined || amount === 0) return '—';
    const symbol = currencyCodeToSymbolMap[currencyCode] || '$';
    const isNegative = amount < 0;
    const formattedValue = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Math.abs(amount));

    return `${isNegative ? '-' : ''}${symbol}${formattedValue}`;
};

const CustomerLedgerReport = ({ onBack, customerId: propCustomerId, customerName: propCustomerName, customerEmail: propCustomerEmail }) => {
    const { showToast } = useToast();
    const location = useLocation();
    const navigate = useNavigate();

    const [isExporting, setIsExporting] = useState(false);

    const [user, setUser] = useState(null);
    const [rawReportData, setRawReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showInternalPostings, setShowInternalPostings] = useState(false);

    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 90);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    const { customerId, customerName, customerEmail } = useMemo(() => {
        const state = location.state || {};
        return {
            customerId: propCustomerId || state.customerId,
            customerName: propCustomerName || state.customerName || 'Unknown Customer',
            customerEmail: propCustomerEmail || state.customerEmail || 'No Email Provided',
        };
    }, [propCustomerId, propCustomerName, propCustomerEmail, location.state]);

    const [currentReceipt, setCurrentReceipt] = useState(null);
    const [isFetchingReceipt, setIsFetchingReceipt] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [openingBalance, setOpeningBalance] = useState(0);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (lcUser) setUser(JSON.parse(lcUser));
        else setError("User not found. Please log in.");
    }, []);

    const fetchReport = useCallback(async () => {
        if (!user || !customerId) return;
        setLoading(true);
        setError(null);
        try {
            const params = { users_id: user.users_id, start_date: startDate, end_date: endDate, customer_id: customerId };
            const response = await fetchCustomerLedgerReport(params);
            console.log(response) // the response object
            setRawReportData(response.transactions || response);
            setOpeningBalance(response.opening_balance ?? 0);
        } catch (err) {
            setError("Failed to load customer ledger report.");
            showToast("Failed to load report.", "error");
        } finally {
            setLoading(false);
        }
    }, [user, customerId, startDate, endDate, showToast]);

    useEffect(() => { if (user && customerId) fetchReport(); }, [user, customerId, fetchReport]);

    const { ledgerEntries, totalBalanceOwed, totalNetBalance, reportCurrency, totalDebits, totalCredits } = useMemo(() => {
        if (!rawReportData) return { ledgerEntries: [], totalBalanceOwed: 0, totalNetBalance: 0, reportCurrency: 'NGN', totalDebits: 0, totalCredits: 0 };

        let runningBalance = openingBalance;
        let debits = 0;
        let credits = 0;
        const currency = rawReportData[0]?.currency_code || 'NGN';

        // 1. Group by Reference so "Splits" are seen as one event
        const groupedByRef = rawReportData.reduce((acc, tx) => {
            const key = tx.ref || tx.transaction_id;
            if (!acc[key]) {
                acc[key] = { ...tx, postings: [...tx.postings] };
            } else {
                // Merge postings from separate IDs (like ID 94, 95, 96) into one list
                acc[key].postings.push(...tx.postings);
            }
            return acc;
        }, {});

        // 2. Process the merged groups
        const entries = Object.values(groupedByRef).reduce((acc, tx) => {
            let customerDebit = 0;
            let customerCredit = 0;

            tx.postings.forEach(p => {
                const isCustomerAccount = p.parent_type === 'accounts_receivable' ||
                    ['1050', '2410', '2103', '1100'].includes(p.account_code) ||
                    ['Accounts Receivable', 'Customer Deposits', 'Prepaid School Fees', 'School Fees Receivable'].includes(p.account_name);
                if (isCustomerAccount) {
                    if (p.posting_type === 'debit') customerDebit += p.amount;
                    if (p.posting_type === 'credit') customerCredit += p.amount;
                }
            });

            const netChange = customerDebit - customerCredit;
            if (customerDebit === 0 && customerCredit === 0) return acc; // Skip pure internal adjustments

            runningBalance += netChange;
            if (netChange > 0) debits += netChange;
            else credits += Math.abs(netChange);

            const auditPostings = tx.postings.filter(p => !['5000', '1060'].includes(p.account_code));

            acc.push({
                ...tx,
                type: tx.document_type || tx.transaction_type.replace(/_/g, ' '),
                date: tx.transaction_date,
                customer_debit: netChange > 0 ? netChange : 0,
                customer_credit: netChange < 0 ? Math.abs(netChange) : 0,
                running_balance: runningBalance,
                postings: auditPostings // Tooltip will now see ALL merged postings here
            });

            return acc;
        }, []);

        return { ledgerEntries: entries, totalBalanceOwed: runningBalance > 0 ? runningBalance : 0, totalNetBalance: runningBalance, reportCurrency: currency, totalDebits: debits, totalCredits: credits };
    }, [rawReportData, openingBalance]);

    const handleExport = async () => {
        if (!user || !customerId) return;
        setIsExporting(true);
        try {
            const params = {
                users_id: user.users_id,
                start_date: startDate,
                end_date: endDate,
                customer_id: customerId
            };

            const blob = await fetchCustomerLedgerExportCSV(params);

            // Create a download link for the blob
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Ledger_${customerName.replace(/\s+/g, '_')}_${startDate}_to_${endDate}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

            showToast("CSV exported successfully", "success");
        } catch (err) {
            showToast("Failed to export CSV", "error");
        } finally {
            setIsExporting(false);
        }
    };

    const handleOrderClick = async (orderId) => {
        if (!orderId || !user) return;

        setIsFetchingReceipt(true);
        setIsModalOpen(true);

        const payload = {
            users_id: user.users_id,
            orders_id: orderId,
        }

        try {
            const response = await salesSourceDocument(payload);
            setCurrentReceipt(response);
        } catch (err) {
            showToast(err.response?.data?.detail || "Error fetching source document", "error");
            setIsModalOpen(false);
        } finally {
            setIsFetchingReceipt(false);
        }

    }

    if (error) return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
            <div className="bg-red-50 p-6 rounded-2xl text-center">
                <Info size={40} className="mx-auto text-red-500 mb-4" />
                <p className="text-gray-800 font-medium mb-4">{error}</p>
                <Button onClick={() => navigate(-1)} variant="outline">Go Back</Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#F8FAFC] pb-12">
            {/* Top Navigation Bar */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-10 px-4 py-3 sm:px-8">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <button onClick={() => navigate(-1)} className="flex items-center text-slate-500 hover:text-slate-800 font-medium transition-colors">
                        <ChevronLeft size={20} className="mr-1" /> Back
                    </button>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            className="text-slate-600 hidden sm:flex items-center justify-center"
                            onClick={handleExport}
                            disabled={isExporting || loading}
                        >
                            {isExporting ? <Loader2 className="animate-spin mr-2" size={18} /> : <Download size={18} className="mr-2" />}
                            Export
                        </Button>
                        <Button onClick={fetchReport} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm flex items-center justify-center">
                            {loading ? <Loader2 className="animate-spin" size={18} /> : <RefreshCw size={18} />}
                            <span className="ml-2 hidden sm:inline">Update Report</span>
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                {/* Header & Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
                    <div className="lg:col-span-2">
                        <div className="flex items-center gap-4 mb-2">
                            <div className="h-12 w-12 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center">
                                <User size={24} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900 leading-tight">{customerName}</h1>
                                <p className="text-slate-500 text-sm">{customerEmail}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between">
                        <div>
                            <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Total Owed</p>
                            <h3 className="text-xl font-bold text-slate-900 mt-1">
                                {formatCurrency(totalBalanceOwed, reportCurrency)}
                            </h3>
                        </div>
                        <div className="h-10 w-10 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center">
                            <TrendingUp size={20} />
                        </div>
                    </div>

                    {/* Total Net Balance (Accounting Perspective) */}
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between">
                        <div>
                            <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Net Ledger Balance</p>
                            <h3 className={`text-xl font-bold mt-1 ${totalNetBalance < 0 ? 'text-emerald-600' : 'text-slate-900'}`}>
                                {formatCurrency(totalNetBalance, reportCurrency)}
                            </h3>
                        </div>
                        <div className="h-10 w-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
                            <HandCoins size={20} />
                        </div>
                    </div>

                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 mb-6">
                    <div className="flex flex-col md:flex-row md:items-end gap-4">
                        <div className="flex-1 grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase ml-1">From Date</label>
                                <div className="relative">
                                    <CalendarDays className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase ml-1">To Date</label>
                                <div className="relative">
                                    <CalendarDays className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between md:justify-end gap-4">
                            <button onClick={() => setShowInternalPostings(!showInternalPostings)} className="text-sm font-medium text-slate-600 hover:text-indigo-600 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 transition-all">
                                {showInternalPostings ? <EyeOff size={16} /> : <Eye size={16} />}
                                {showInternalPostings ? 'Simplified View' : 'Audit View'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Table / Mobile Cards */}
                <Card className="overflow-hidden border-slate-200 shadow-xl rounded-2xl">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-24">
                            <Loader2 className="animate-spin h-10 w-10 text-indigo-600 mb-4" />
                            <p className="text-slate-500 font-medium">Reconciling Ledger Entries...</p>
                        </div>
                    ) : ledgerEntries.length === 0 ? (
                        <div className="py-24 text-center">
                            <FileText size={48} className="mx-auto text-slate-200 mb-4" />
                            <p className="text-slate-500">No activity found for this period.</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop View */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                            {/* <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Date</th> */}
                                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Transaction</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Reference</th>
                                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Debit (+)</th>
                                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Credit (-)</th>
                                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Balance</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase tracking-wider">Info</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {ledgerEntries.map((entry, idx) => (
                                            <React.Fragment key={idx}>
                                                <tr className="hover:bg-slate-50/50 transition-colors group">
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm font-bold text-slate-900">{entry.type}</div>
                                                        <div className="text-xs text-slate-500">{formatDate(entry.date)}</div>
                                                    </td>
                                                    <td className="px-6 py-4 max-w-xs">
                                                        {entry.orders_id ? (
                                                            <button
                                                                onClick={() => handleOrderClick(entry.orders_id)}
                                                                className="text-indigo-600 font-medium hover:underline hover:text-indigo-600 transition-colors cursor-pointer"
                                                            >
                                                                <div className="text-sm font-mono font-medium">
                                                                    {entry.ref}
                                                                </div>
                                                            </button>
                                                        ) : (
                                                            <div className="text-sm text-slate-600 font-mono font-medium">
                                                                {entry.ref}
                                                            </div>
                                                        )}
                                                        <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                                                            {entry.description}
                                                        </div>
                                                    </td>                                                    <td className="px-6 py-4 text-right text-sm font-medium text-slate-900">
                                                        {entry.customer_debit > 0 ? (
                                                            <span className="text-orange-600">+{formatCurrency(entry.customer_debit, entry.currency_code)}</span>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-medium text-slate-900">
                                                        {entry.customer_credit > 0 ? (
                                                            <span className="text-emerald-600">-{formatCurrency(entry.customer_credit, entry.currency_code)}</span>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900 bg-slate-50/30">
                                                        {formatCurrency(entry.running_balance, entry.currency_code)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center relative group">
                                                        <Info size={16} className="mx-auto text-slate-400 cursor-help hover:text-indigo-600 transition-colors" />

                                                        {/* Tooltip implementation */}
                                                        {/* <div className="invisible group-hover:visible absolute z-50 right-full mr-2 top-0">
                                                            <TransactionTooltip entry={entry} />
                                                        </div> */}
                                                        <div className="invisible group-hover:visible absolute z-50 right-full mr-3 top-0 transition-all duration-200">
                                                            {entry && <TransactionTooltip entry={entry} />}
                                                        </div>
                                                    </td>
                                                </tr>
                                                {/* {showInternalPostings && entry.postings?.map((post, pIdx) => (
                                                    <tr key={pIdx} className="bg-indigo-50/30 text-[11px]">
                                                        <td colSpan={2} className="px-10 py-2 italic text-slate-500">
                                                            ↳ {post.account_name}
                                                        </td>
                                                        <td className="px-6 py-2 text-right text-slate-400">
                                                            {post.type === 'debit' ? formatCurrency(post.amount, entry.currency_code) : ''}
                                                        </td>
                                                        <td className="px-6 py-2 text-right text-slate-400">
                                                            {post.type === 'credit' ? formatCurrency(post.amount, entry.currency_code) : ''}
                                                        </td>
                                                        <td></td>
                                                    </tr>
                                                ))} */}
                                                {showInternalPostings && entry.postings?.map((post, pIdx) => (
                                                    <tr key={pIdx} className="bg-slate-50/80 text-[11px] border-l-4 border-indigo-400">
                                                        <td className="px-10 py-2 italic text-slate-500">
                                                            <span className="font-mono text-[10px] bg-slate-200 px-1 rounded mr-2">
                                                                {post.account_code}
                                                            </span>
                                                            {post.account_name}
                                                        </td>
                                                        <td className="px-6 py-2 text-slate-400 italic">
                                                            {/* Logic to describe the posting type if needed */}
                                                            {post.root_type}
                                                        </td>
                                                        <td className="px-6 py-2 text-right text-slate-500">
                                                            {post.posting_type === 'debit' ? formatCurrency(post.amount, entry.currency_code) : '—'}
                                                        </td>
                                                        <td className="px-6 py-2 text-right text-slate-500">
                                                            {post.posting_type === 'credit' ? formatCurrency(post.amount, entry.currency_code) : '—'}
                                                        </td>
                                                        <td className="px-6 py-2 bg-slate-100/30"></td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-slate-900 text-white">
                                        <tr>
                                            <td colSpan={2} className="px-6 py-5 text-sm font-bold uppercase tracking-wider">Summary Totals</td>
                                            <td className="px-6 py-5 text-right text-sm font-bold">{formatCurrency(totalDebits, reportCurrency)}</td>
                                            <td className="px-6 py-5 text-right text-sm font-bold">{formatCurrency(totalCredits, reportCurrency)}</td>
                                            <td className="px-6 py-5 text-right text-base font-black text-indigo-300">{formatCurrency(totalBalanceOwed, reportCurrency)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Mobile View */}
                            <div className="md:hidden divide-y divide-slate-100">
                                {ledgerEntries.map((entry, idx) => (
                                    <div key={idx} className="p-4 space-y-3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="text-[10px] font-bold uppercase text-slate-400">{formatDate(entry.date, false)}</span>
                                                <h4 className="font-bold text-slate-900">{entry.type}</h4>
                                                {/* <p className="text-xs text-slate-500 font-mono">{entry.ref}</p> */}
                                                {entry.orders_id ? (
                                                    <button
                                                        onClick={() => handleOrderClick(entry.orders_id)}
                                                        className="text-xs text-indigo-600 font-mono font-medium hover:underline text-left"
                                                    >
                                                        {entry.ref}
                                                    </button>
                                                ) : (
                                                    <p className="text-xs text-slate-500 font-mono">{entry.ref}</p>
                                                )}
                                                {entry.description && (
                                                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                                                        {entry.description}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-slate-400">Balance</p>
                                                <p className="font-bold text-slate-900">{formatCurrency(entry.running_balance, entry.currency_code)}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            {entry.customer_debit > 0 && (
                                                <div className="flex-1 bg-orange-50 p-2 rounded-lg border border-orange-100">
                                                    <p className="text-[10px] text-orange-600 font-bold uppercase">Debit</p>
                                                    <p className="text-sm font-bold text-orange-700">{formatCurrency(entry.customer_debit, entry.currency_code)}</p>
                                                </div>
                                            )}
                                            {entry.customer_credit > 0 && (
                                                <div className="flex-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100">
                                                    <p className="text-[10px] text-emerald-600 font-bold uppercase">Credit</p>
                                                    <p className="text-sm font-bold text-emerald-700">{formatCurrency(entry.customer_credit, entry.currency_code)}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div className="p-6 bg-slate-900 text-white space-y-2">
                                    <div className="flex justify-between text-xs opacity-70">
                                        <span>Total Debits</span>
                                        <span>{formatCurrency(totalDebits, reportCurrency)}</span>
                                    </div>
                                    <div className="flex justify-between text-xs opacity-70">
                                        <span>Total Credits</span>
                                        <span>{formatCurrency(totalCredits, reportCurrency)}</span>
                                    </div>
                                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-700">
                                        <span>Balance Due</span>
                                        <span className="text-indigo-400">{formatCurrency(totalBalanceOwed, reportCurrency)}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </Card>
            </div>
            <Modal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setCurrentReceipt(null); }}
                title="Source Document"
                size="wide"
            >
                <SalesSourceDocument receipt={currentReceipt} isLoading={isFetchingReceipt} formatDate={formatDate} formatCurrency={formatCurrency} />
            </Modal>
        </div>
    );
};

export default CustomerLedgerReport;